import { Injectable } from '@angular/core';

import { ApiService }                      from '../api/api.service';
import { IntelligenceAiAnswerModel }       from './typings/intelligence.ai-answer';
import { IntelligenceAskTurnModel }        from './typings/intelligence.ask-turn';
import { IntelligenceNarrativeFactsModel } from './typings/intelligence.narrative-facts';
import { IntelligenceService }             from './intelligence.service';

const TOP_LABELS = 3;

// request timeout backstop
const AI_TIMEOUT_MS = 120000;

@Injectable({
  providedIn: 'root'
})
export class IntelligenceNarrativeService {
  constructor (
    private apiService         : ApiService,
    private intelligenceService: IntelligenceService
  ) {}

  // ask, with the transcript replayed
  ask ( question : string, history : Array<IntelligenceAskTurnModel> = [] ) : Promise<IntelligenceAiAnswerModel> {
    const trimmed = Array.from(question || '').filter(ch => ch.charCodeAt(0) > 31 && ch.charCodeAt(0) !== 127).join('').trim().slice(0, 300);

    if ( !trimmed ) {
      return Promise.resolve(this.answer(null, ''));
    }

    return this.timeBoxed(this.apiService.post('/intelligence/ask', { history, question: trimmed }))
      .then(( response : any ) => this.answer(response, response && response.answer));
  }

  // summarises only the figures the tab shows, so the summary matches what the owner sees
  summariseTab ( tab : string ) : Promise<IntelligenceAiAnswerModel> {
    return this.factsFor(tab).then(facts => Object.keys(facts).length
      ? this.summarise(facts)
      : this.answer(null, ''));
  }

  summarise ( facts : IntelligenceNarrativeFactsModel ) : Promise<IntelligenceAiAnswerModel> {
    return this.timeBoxed(this.apiService.post('/intelligence/narrative', { facts }))
      .then(( response : any ) => this.answer(response, response && response.narrative));
  }

  // rejects on timeout so a slow reply cannot land in a card that has moved on
  private timeBoxed ( call : Promise<any> ) : Promise<any> {
    let timer : any = null;

    const ceiling = new Promise(( _resolve, reject ) => {
      timer = setTimeout(() => reject(new Error('The AI request timed out')), AI_TIMEOUT_MS);
    });

    return Promise.race([ call, ceiling ]).finally(() => clearTimeout(timer));
  }

  // running out of AI allowance is a normal reply, passed on as state rather than an error
  private answer ( response : any, text : string ) : IntelligenceAiAnswerModel {
    return {
      limitReached    : !!( response && response.limit_reached ),
      text            : text || '',
      upgradeAvailable: !!( response && response.upgrade_available )
    };
  }

  private factsFor ( tab : string ) : Promise<IntelligenceNarrativeFactsModel> {
    const range = this.intelligenceService.range;
    const dates = { end_date: range.end_date, start_date: range.start_date };
    const filtered = ( dataset : string ) : object => {
      const dimensions = this.filteredBy(dataset);

      return dimensions.length
        ? { ...dates, filtered_by: dimensions }
        : dates;
    };

    switch (tab) {
      case 'customers':
        return this.intelligenceService.getCustomers(this.intelligenceService.filtersFor('customers')).then(( data : any ) => {
          const sources = data.sources || {};
          const series = data.series || [];

          return {
            ...filtered('customers'),
            days                : series.length,
            new_customers       : this.total(series, 'new_customers'),
            avg_visits_per_year : data.avg_visits_per_year || 0,
            retention_pct       : data.retention_rate_pct || 0,
            returning_customers : this.total(series, 'returning_customers_invoiced'),
            top_customer_sources: this.topKeys(sources)
          };
        });

      case 'mechanics':
        return this.intelligenceService.getMechanics(( this.intelligenceService.filtersFor('mechanics') || {} ).mechanic).then(( data : any ) => {
          const mechanics = data.mechanics || [];

          return {
            ...filtered('mechanics'),
            jobs_completed     : this.total(mechanics, 'jobs_completed'),
            mechanics_count    : mechanics.length,
            mechanics_under_100: mechanics.filter(( item : any ) => item.recovery_rate < 100).length
          };
        });

      case 'money':
        return this.intelligenceService.getFinancials().then(( data : any ) => {
          const payables = data.payables_aging || {};
          const receivables = data.receivables_aging || {};
          const series = data.series || [];
          const revenue = this.total(series, 'revenue');
          const grossProfit = this.total(series, 'gross_profit');

          return {
            ...dates,
            days                : series.length,
            gross_profit        : grossProfit,
            margin_pct          : this.margin(revenue, grossProfit),
            payables_outstanding: this.outstanding(payables),
            payments            : this.total(series, 'payments'),
            receivables_over_90 : this.outstanding(receivables, 'days_90_plus'),
            receivables_total   : this.outstanding(receivables),
            revenue
          };
        });

      case 'operations':
        return this.intelligenceService.getOperations().then(( data : any ) => ({
          ...dates,
          booking_conversion_pct: data.booking_conversion_rate || 0,
          quote_close_pct       : data.quotes_created
            ? Math.round(( data.quotes_won / data.quotes_created ) * 1000) / 10
            : 0,
          bookings_created      : this.total(data.series || [], 'bookings_created'),
          reminder_bookings     : data.reminder_bookings || 0,
          reminders_sent        : data.reminders_sent || 0
        }));

      case 'opportunities':
        return this.intelligenceService.getOpportunities().then(( data : any ) => ({
          ...dates,
          declined_work_value    : Math.round(( data.declined_work || [] ).reduce(( sum : number, row : any ) => sum + ( row.value_ex_tax || 0 ), 0)),
          lapsed_customers       : ( data.lapsed_customers || [] ).length,
          overdue_services       : ( data.overdue_services || [] ).length,
          unconverted_quote_value: Math.round(( data.unconverted_quotes || [] ).reduce(( sum : number, row : any ) => sum + ( row.value_ex_tax || 0 ), 0))
        }));

      case 'products':
        return this.intelligenceService.getProducts(this.intelligenceService.filtersFor('products')).then(( data : any ) => ({
          ...filtered('products'),
          revenue           : Math.round(( data.product_income || [] ).reduce(( sum : number, group : any ) => sum + ( group.revenue_ex_tax || 0 ), 0)),
          top_product_groups: ( data.product_income || [] ).slice(0, TOP_LABELS).map(( group : any ) => group.label)
        }));

      case 'purchasing':
        return this.intelligenceService.getPurchasing(this.intelligenceService.filtersFor('purchasing')).then(( data : any ) => ({
          ...filtered('purchasing'),
          open_order_count    : data.open_order_count || 0,
          open_order_value    : data.open_order_value || 0,
          payables_outstanding: this.outstanding(data.payables_aging || {}),
          purchasing_credits  : data.credits_ex_tax || 0,
          purchasing_spend    : data.spend_ex_tax || 0,
          top_suppliers       : this.topKeys(data.spend_by_supplier || {})
        }));

      case 'vehicles':
        return this.intelligenceService.getVehicles(this.intelligenceService.filtersFor('vehicles')).then(( data : any ) => ({
          ...filtered('vehicles'),
          top_vehicle_makes: ( data.makes || [] ).slice(0, TOP_LABELS).map(( make : any ) => make.vehicle_make)
        }));

      case 'overview':
        return this.overviewFacts(dates);

      // tabs without a fact mapping get no summary rather than one about the overview figures
      default:
        return Promise.resolve({});
    }
  }

  // names the filtered dimensions, never their values, so no customer or supplier reaches the model
  private filteredBy ( dataset : string ) : Array<string> {
    const filters = this.intelligenceService.filtersFor(dataset) || {};

    return Object.keys(filters)
      .filter(key => !!filters[ key ])
      .sort()
      .map(key => key.replace(/_/g, ' '));
  }

  private margin ( revenue : number, grossProfit : number ) : number {
    return revenue
      ? Math.round(( grossProfit / revenue ) * 1000) / 10
      : 0;
  }

  private outstanding ( aging : any, bucket = '' ) : number {
    const buckets = bucket
      ? [ bucket ]
      : Object.keys(aging);

    return Math.round(buckets.reduce(( sum, key ) => {
      const entry = aging[ key ];

      return sum + ( entry && entry.outstanding !== undefined
        ? entry.outstanding
        : entry || 0 );
    }, 0));
  }

  private overviewFacts ( dates : { end_date : string; start_date : string } ) : Promise<IntelligenceNarrativeFactsModel> {
    return this.intelligenceService.getSummary().then(( summary : any ) => {
      const kpi = ( key : string ) : number => summary.kpis && summary.kpis[ key ]
        ? Math.round(summary.kpis[ key ].value)
        : 0;

      const revenue = kpi('revenue');
      const grossProfit = kpi('gross_profit');

      return {
        ...dates,
        avg_invoice_value: kpi('avg_invoice_value'),
        gross_profit     : grossProfit,
        invoice_count    : kpi('invoice_count'),
        jobs_completed   : kpi('jobs_completed'),
        margin_pct       : this.margin(revenue, grossProfit),
        new_customers    : kpi('new_customers'),
        payments         : kpi('payments'),
        receivables_total: Math.round(summary.receivables ? summary.receivables.outstanding : 0),
        revenue,
        wip_job_count    : summary.work_in_progress ? summary.work_in_progress.job_count : 0,
        wip_value_ex_tax : Math.round(summary.work_in_progress ? summary.work_in_progress.value_ex_tax : 0)
      };
    });
  }

  private topKeys ( counts : { [ label : string ] : number } ) : Array<string> {
    return Object.keys(counts)
      .filter(label => label !== 'unknown')
      .sort(( first, second ) => counts[ second ] - counts[ first ])
      .slice(0, TOP_LABELS);
  }

  private total ( rows : Array<any>, field : string ) : number {
    return Math.round(( rows || [] ).reduce(( sum, row ) => sum + ( row[ field ] || 0 ), 0));
  }
}
